import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CircularLoading from "./components/CircularLoading";

// Static Imports
import ProtectedRoute from "./views/ProtectedRoute";
import { toLocalizedPath } from "./utils/i18nRoute";
import PublicSiteLayout from "./layouts/PublicSiteLayout";

// Lazy-loaded Views with auto-reload retry on deployment ChunkLoadError
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenReloaded = sessionStorage.getItem("chunk_has_reloaded");
    try {
      const component = await componentImport();
      sessionStorage.removeItem("chunk_has_reloaded");
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenReloaded) {
        sessionStorage.setItem("chunk_has_reloaded", "true");
        window.location.reload();
        return new Promise(() => {}); // Pause component render while page reloads
      }
      throw error;
    }
  });

const LandingPage = lazyWithRetry(() => import("./views/public/LandingPage.js"));
const LoginPage = lazyWithRetry(() => import("./views/auth/LoginPage.js"));
const PrivacyPolicy = lazyWithRetry(() => import("./views/public/PrivacyPolicy.js"));
const AboutUs = lazyWithRetry(() => import("./views/public/AboutUs.js"));
const MyProfile = lazyWithRetry(() => import("./views/user/MyProfile"));
const BookingHistory = lazyWithRetry(() => import("./views/booking/BookingHistory"));
const Dashboard = lazyWithRetry(() => import("./features/dashboard/DashboardPage"));
const CarTypes = lazyWithRetry(() => import("./views/admin/CarTypes"));
const GlobalPricingManager = lazyWithRetry(() => import("./views/admin/GlobalPricingManager"));
const AddBookings = lazyWithRetry(() => import("./views/booking/AddBookings"));
const Promos = lazyWithRetry(() => import("./views/admin/Promos"));
const Riders = lazyWithRetry(() => import("./views/admin/Riders"));
const Drivers = lazyWithRetry(() => import("./views/admin/Drivers"));
const FleetAdmins = lazyWithRetry(() => import("./views/admin/FleetAdmins"));
const CreateAdmin = lazyWithRetry(() => import("./views/admin/CreateAdmin"));
const Users = lazyWithRetry(() => import("./views/admin/Users"));
const CustomerDetails = lazyWithRetry(() => import("views/admin/CustomerDetails.js"));
const Notifications = lazyWithRetry(() => import("./views/admin/Notifications"));
const Settings = lazyWithRetry(() => import("./views/admin/Settings.js"));
const AppInformation = lazyWithRetry(() => import("./views/admin/AppInformation"));
const GeneralSettings = lazyWithRetry(() => import("./views/admin/GeneralSettings"));
const VacationSettings = lazyWithRetry(() => import("./views/admin/VacationSettings"));
const SurchargeSettings = lazyWithRetry(() => import("./views/admin/SurchargeSettings"));
const LanguageSetting = lazyWithRetry(() => import("./views/admin/LanguageSetting"));
const SMTPSettings = lazyWithRetry(() => import("./views/admin/SMTPSettings"));
const GoogleKeysSetting = lazyWithRetry(() => import("./views/admin/GoogleKeysSetting"));
const SmsSettings = lazyWithRetry(() => import("./views/admin/SmsSettings"));
const CancellationReasons = lazyWithRetry(() => import("./views/admin/CancellationReasons"));
const TelegramSettings = lazyWithRetry(() => import("./views/admin/TelegramSettings"));
const Complain = lazyWithRetry(() => import("views/admin/Complain.js"));
const AddMoney = lazyWithRetry(() => import("./views/admin/AddMoney"));
const Withdraws = lazyWithRetry(() => import("./views/admin/Withdraws"));
const AllReports = lazyWithRetry(() => import("./views/admin/AllReports"));
const WebBooking = lazyWithRetry(() => import("./views/booking/WebBooking"));
const GoogleCalendarSettings = lazyWithRetry(() => import("./views/admin/GoogleCalendarSettings"));
const DriverSettings = lazyWithRetry(() => import("./views/user/DriverSettings"));
const ContactUs = lazyWithRetry(() => import("./views/public/ContactUs"));
const UserWallet = lazyWithRetry(() => import("./views/user/UserWallet"));
const UserInvoices = lazyWithRetry(() => import("./views/user/UserInvoices"));
const CarsList = lazyWithRetry(() => import("./views/admin/CarsList"));
const TermCondition = lazyWithRetry(() => import("views/public/TermCondition.js"));
const Cookies = lazyWithRetry(() => import("views/public/Cookies.js"));
const Sos = lazyWithRetry(() => import("./views/public/Sos"));
const DriverDetails = lazyWithRetry(() => import("views/admin/DriverDetails.js"));
const Error = lazyWithRetry(() => import("views/public/Error.js"));
const BookingDetails = lazyWithRetry(() => import("views/booking/BookingDetails.js"));
const UserDocuments = lazyWithRetry(() => import("views/user/UserDocuments.js"));
const AddNotifications = lazyWithRetry(() => import("views/admin/AddNotifications.js"));
const EditPromo = lazyWithRetry(() => import("views/admin/EditPromo.js"));
const EditCar = lazyWithRetry(() => import("views/admin/EditCar.js"));
const EditCarType = lazyWithRetry(() => import("views/admin/EditCarType.js"));
const EditUser = lazyWithRetry(() => import("./views/admin/EditUser.js"));
const PaymentSettings = lazyWithRetry(() => import("views/admin/PaymentSettings.js"));
const ZoneManager = lazyWithRetry(() => import("views/admin/ZoneManager.js"));
const ZoneLearningDashboard = lazyWithRetry(() => import("views/admin/ZoneLearningDashboard.js"));
const ReliabilityDashboard = lazyWithRetry(() => import("views/admin/ReliabilityDashboard.js"));
const PricingSimulator = lazyWithRetry(() => import("views/admin/PricingSimulator.js"));
const Overview = lazyWithRetry(() => import("views/booking/Overview.js"));
const DriverOverview = lazyWithRetry(() => import("views/booking/DriverOverview.js"));
const DriverEarnings = lazyWithRetry(() => import("views/user/DriverEarnings.js"));
const CustomerRegistry = lazyWithRetry(() => import("./views/admin/CustomerRegistry.js"));
const CustomerDetail = lazyWithRetry(() => import("./views/admin/CustomerDetail.js"));
const ManualDocumentForm = lazyWithRetry(() => import("./views/admin/ManualDocumentForm.js"));

// [V1.8.0] Role-based Overview wrapper
function RoleBasedOverview() {
  const auth = useSelector((state) => state.auth);
  const role = auth?.profile?.usertype;

  if (role === "driver") {
    return <DriverOverview />;
  }
  return <Overview />;
}

// [V1.9.5] Stable localized redirect
function RedirectToLocalized({ to, children }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const target = toLocalizedPath(to, i18n.language);

  // If we are already at the target, render the children directly
  // This handles the "FR at root" case where /privacy-policy is already canonical for FR.
  if (location.pathname === target) {
    return children;
  }
  return <Navigate to={target} replace />;
}

// [V1.9.5] List of public slugs that should redirect from /fr/slug to /slug
const PUBLIC_FR_REDIRECT_SLUGS = [
  "privacy-policy",
  "about-us",
  "contact-us",
  "term-condition",
  "cookies",
  "login",
  "booking"
];

function AppRoutes(props) {
  const { darkMode, toggleDarkMode, themeMode } = props;

  return (
    <Suspense fallback={<CircularLoading />}>
      <Routes>
        <Route
          path="dashboard"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Dashboard darkMode={darkMode} />
            </ProtectedRoute>
          }
        />
        <Route
          path="overview"
          element={
            <ProtectedRoute permit={"customer,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <RoleBasedOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="bookings"
          element={
            <ProtectedRoute permit={"customer,admin,driver,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <BookingHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="bookings/bookingdetails/:id"
          element={
            <ProtectedRoute permit={"customer,admin,driver,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <BookingDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="guest-recovery/:id"
          element={<BookingDetails guestMode={true} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />}
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute permit={"customer,admin,driver,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="cartypes"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CarTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path="cartypes/global-pricing"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <GlobalPricingManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="cars"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CarsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="addbookings"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,customer,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <AddBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="promos"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Promos />
            </ProtectedRoute>
          }
        />
        <Route
          path="gcal-settings"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <GoogleCalendarSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute permit={"driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <DriverSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/clients"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Riders />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/chauffeurs"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Drivers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/gestionnaires"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <FleetAdmins />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/administrateurs"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CreateAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/customerdetails/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CustomerDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/driverdetails/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <DriverDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/userdocuments/:rId/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <UserDocuments />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="addtowallet"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <AddMoney />
            </ProtectedRoute>
          }
        />
        <Route
          path="driverearnings"
          element={
            <ProtectedRoute permit={"driver"}>
              <DriverEarnings />
            </ProtectedRoute>
          }
        />
        <Route
          path="userwallet"
          element={
            <ProtectedRoute permit={"customer,driver,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <UserWallet />
            </ProtectedRoute>
          }
        />
        <Route
          path="userinvoices"
          element={
            <ProtectedRoute permit={"customer,admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <UserInvoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="customers"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CustomerRegistry />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/:customerId"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CustomerDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="customers/:customerId"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CustomerDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="create-document"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <ManualDocumentForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="edit-document/:documentType/:documentId"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <ManualDocumentForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="withdraws"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Withdraws />
            </ProtectedRoute>
          }
        />
        <Route
          path="sos"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Sos />
            </ProtectedRoute>
          }
        />
        <Route
          path="complain"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Complain />
            </ProtectedRoute>
          }
        />
        <Route
          path="allreports"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <AllReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/app-info"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <AppInformation />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/general"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <GeneralSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/vacation"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <VacationSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/surcharges"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <SurchargeSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/language"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <LanguageSetting />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/smtp"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <SMTPSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/sms"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <SmsSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/google-keys"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <GoogleKeysSetting />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/cancellation-reasons"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <CancellationReasons />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/telegram"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <TelegramSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="paymentsettings"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <PaymentSettings />
            </ProtectedRoute>
          }
        />

        {/* Public Layout Routes */}
        <Route
          path="contact-us"
          element={
            <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
              <ContactUs />
            </PublicSiteLayout>
          }
        />
        <Route
          path="about-us"
          element={
            <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
              <AboutUs />
            </PublicSiteLayout>
          }
        />
        <Route
          path="privacy-policy"
          element={
            <RedirectToLocalized to="/privacy-policy">
              <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
                <PrivacyPolicy />
              </PublicSiteLayout>
            </RedirectToLocalized>
          }
        />
        <Route
          path="term-condition"
          element={
            <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
              <TermCondition />
            </PublicSiteLayout>
          }
        />
        <Route
          path="cookies"
          element={
            <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
              <Cookies />
            </PublicSiteLayout>
          }
        />
        <Route
          path="login"
          element={
            <PublicSiteLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeMode={themeMode}>
              <LoginPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            </PublicSiteLayout>
          }
        />

        <Route
          path="users/edituser/:usertype"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/edituser/:usertype/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications/addnotifications"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <AddNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="cars/editcar"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditCar />
            </ProtectedRoute>
          }
        />
        <Route
          path="cars/editcar/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditCar />
            </ProtectedRoute>
          }
        />
        <Route
          path="promos/editpromo"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditPromo />
            </ProtectedRoute>
          }
        />
        <Route
          path="promos/editpromo/:id"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditPromo />
            </ProtectedRoute>
          }
        />
        <Route
          path="cartypes/editcartype"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditCarType />
            </ProtectedRoute>
          }
        />
        <Route
          path="cartypes/editcartype/:id"
          element={
            <ProtectedRoute permit={"admin,fleetadmin,driver"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <EditCarType />
            </ProtectedRoute>
          }
        />
        <Route
          path="zonemanager"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <ZoneManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="zone-intelligence"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <ZoneLearningDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="reliability"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <ReliabilityDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/simulator"
          element={
            <ProtectedRoute permit={"admin"} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
              <PricingSimulator />
            </ProtectedRoute>
          }
        />
        <Route path="booking" element={<WebBooking />} />
        <Route path="embed/booking" element={<WebBooking embedMode={true} />} />

        {/* [V1.9.5] FR Aliases Redirection */}
        <Route path="fr" element={<Navigate to="/" replace />} />
        {PUBLIC_FR_REDIRECT_SLUGS.map((slug) => (
          <Route
            key={slug}
            path={`fr/${slug}`}
            element={<Navigate to={`/${slug}`} replace />}
          />
        ))}

        {/* Landing Page with forceSolidHeader={false} */}
        <Route
          path=""
          element={
            <PublicSiteLayout
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              themeMode={themeMode}
            >
              <LandingPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            </PublicSiteLayout>
          }
        />
        <Route path="*" element={<Error />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
